/* @jsxRuntime automatic */
/* @jsxImportSource @superweb/css */

import { useRef, type PointerEvent } from "react";
import { mergeProps, useFocusRing, useHover, useLink } from "react-aria";

import { useIsMobile, useTypo, useUiColors } from "@superweb/ui";
import { cssFns } from "@superweb/css";

import bicycleCourier from "../images/bicycle-courier-profession.svg";
import carCourier from "../images/car-courier-profession.svg";
import individualEntrepreneur from "../images/individual-entrepreneur.svg";
import intercityDriver from "../images/intercity-driver-profession.svg";
import motorcycleCourier from "../images/motorcycle-courier-profession.svg";
import parkEmployee from "../images/park-employee.svg";
import personalCarTaxi from "../images/personal-car-taxi-profession.svg";
import rentalCarTaxi from "../images/rental-car-taxi-profession.svg";
import scooterCourier from "../images/scooter-courier-profession.svg";
import selfemployed from "../images/selfemployed.svg";
import shiftWorker from "../images/shift-worker-profession.svg";
import truckCourier from "../images/truck-courier-profession.svg";
import walkingCourier from "../images/walking-courier-profession.svg";

const iconMap = {
  "rental-car-taxi-profession": rentalCarTaxi,
  "personal-car-taxi-profession": personalCarTaxi,
  "intercity-driver-profession": intercityDriver,
  "walking-courier-profession": walkingCourier,
  "bicycle-courier-profession": bicycleCourier,
  "scooter-courier-profession": scooterCourier,
  "motorcycle-courier-profession": motorcycleCourier,
  "truck-courier-profession": truckCourier,
  "shift-worker-profession": shiftWorker,
  "car-courier-profession": carCourier,
  "park-employee": parkEmployee,
  selfemployed: selfemployed,
  "individual-entrepreneur": individualEntrepreneur,
} as const;

export const LinkPanel = ({
  onPress,
  icon,
  label,
  description,
  subLabel,
  link,
}: {
  onPress: () => void;
  icon: keyof typeof iconMap;
  label: string;
  subLabel?: string;
  description: string;
  link?: {
    href: string;
    onClick?: (event: PointerEvent<HTMLAnchorElement>) => void;
    external?: boolean;
  };
}) => {
  const typo = useTypo();
  const isMobile = useIsMobile();
  const uiColors = useUiColors();

  const ref = useRef<HTMLAnchorElement>(null);
  const { linkProps } = useLink(
    {
      "aria-label": label,
      onPress,
      href: link?.href,
      ...(link?.external && { target: "_blank", rel: "noreferrer" }),
    },
    ref,
  );
  const { hoverProps, isHovered } = useHover({});
  const { focusProps, isFocusVisible: isFocused } = useFocusRing({});

  return (
    <a
      {...mergeProps(hoverProps, focusProps, linkProps)}
      ref={ref}
      css={{
        ...cssFns.margin("0"),
        ...cssFns.padding("0"),
        color: uiColors.text,
        outlineStyle: "none",
        backgroundColor: "transparent",
        textDecorationLine: "none",
        ...(isMobile
          ? {
              width: "160px",
              minHeight: "200px",
              flexDirection: "column",
              alignItems: "center",
              gridTemplateRows: "1fr auto",
            }
          : {
              width: "100%",
              minHeight: "234px",
              gridTemplateColumns: "auto 1fr",
            }),
        cursor: "pointer",
        display: "grid",
        textAlign: "start",
        boxSizing: "border-box",
        boxShadow:
          isHovered || isFocused
            ? "inset 0 0 0 2px " + uiColors.focus
            : "inset 0 0 0 1px " + uiColors.line,
        ...cssFns.border({
          style: "none",
          radius: "20px",
        }),
      }}
    >
      <div
        css={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBlockStart: isMobile ? "24px" : "0",
        }}
      >
        <img
          src={iconMap[icon]}
          css={{
            objectFit: "contain",
            maxWidth: isMobile ? "112px" : "248px",
            width: "100%",
          }}
          alt=""
        />
      </div>
      <div
        css={{
          flexGrow: "1",
          rowGap: "16px",
          display: "flex",
          whiteSpace: "pre-wrap",
          flexDirection: "column",
          justifyContent: "space-between",
          paddingBlockStart: isMobile ? "18px" : "20px",
          paddingBlockEnd: isMobile ? "12px" : "16px",
          paddingInlineStart: isMobile ? "16px" : "8px",
          paddingInlineEnd: "16px",

          ...cssFns.overflow("hidden"),
          ...typo({
            level: isMobile ? "caption1" : "body1",
            density: isMobile ? "tight" : "normal",
            weight: isMobile ? "medium" : "regular",
          }),

          ...(isMobile && {
            textAlign: "center",
          }),
        }}
      >
        <div>
          <div>{label}</div>
          {!isMobile && subLabel ? (
            <div
              css={{
                ...typo({
                  level: "body2",
                  density: "normal",
                  weight: "regular",
                }),
                color: uiColors.textMinor,
              }}
            >
              {subLabel}
            </div>
          ) : null}
        </div>
        {!isMobile && <div>{description}</div>}
      </div>
    </a>
  );
};
